// components/common/DateRangeSelector.jsx - MOBILE RESPONSIVE VERSION

import React, { useState, useEffect } from 'react';
import { CalendarIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDateForDisplay = (dateString) => {
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const getWeekStart = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day;
  return new Date(d.setDate(diff));
};

const getWeekEnd = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + 6;
  return new Date(d.setDate(diff));
};

// ============================================================================
// PRESET GENERATORS
// ============================================================================

const generateYearOptions = (currentYear, showFutureYears = false) => {
  const yearOptions = {};
  
  if (showFutureYears) {
    // ✅ For P&L Playground: show past + current + future
    // Generates: 2025, 2026, 2027, 2028
    const startYear = currentYear - 1;
    const endYear = currentYear + 2;
    
    for (let year = startYear; year <= endYear; year++) {
      const yearStart = new Date(year, 0, 1);
      const yearEnd = new Date(year, 11, 31);
      
      yearOptions[`year-${year}`] = {
        label: year.toString(),
        start_date: formatLocalDate(yearStart),
        end_date: formatLocalDate(yearEnd),
        type: 'year',
        year: year
      };
    }
  } else {
    // ✅ For Finance Dashboard: show current + past years only
    // Generates: 2026, 2025, 2024
    const numberOfPastYears = 2; // Show 4 years of history
    
    for (let i = 0; i <= numberOfPastYears; i++) {
      const year = currentYear - i;
      const yearStart = new Date(year, 0, 1);
      const yearEnd = new Date(year, 11, 31);
      
      yearOptions[`year-${year}`] = {
        label: year.toString(),
        start_date: formatLocalDate(yearStart),
        end_date: formatLocalDate(yearEnd),
        type: 'year',
        year: year
      };
    }
  }
  
  return yearOptions;
};

const generateMonthOptions = (year, typePrefix) => {
  return Array.from({ length: 12 }, (_, i) => {
    const monthStart = new Date(year, i, 1);
    const monthEnd = new Date(year, i + 1, 0);
    const monthName = monthStart.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    
    return {
      [`${typePrefix}-${i}`]: {
        label: monthName,
        start_date: formatLocalDate(monthStart),
        end_date: formatLocalDate(monthEnd),
        type: typePrefix === 'month' ? 'monthly' : 'monthly-prev'
      }
    };
  }).reduce((acc, month) => ({ ...acc, ...month }), {});
};

const generateStandardPresets = (today, currentYear, currentMonth) => {
  const thisWeekStart = getWeekStart(today);
  const thisWeekEnd = getWeekEnd(today);
  const lastWeekStart = new Date(thisWeekStart);
  lastWeekStart.setDate(lastWeekStart.getDate() - 7);
  const lastWeekEnd = new Date(thisWeekEnd);
  lastWeekEnd.setDate(lastWeekEnd.getDate() - 7);

  return {
    // Current periods
    'this-week': {
      label: 'This Week',
      start_date: formatLocalDate(thisWeekStart),
      end_date: formatLocalDate(thisWeekEnd),
      type: 'preset'
    },
    'last-week': {
      label: 'Last Week',
      start_date: formatLocalDate(lastWeekStart),
      end_date: formatLocalDate(lastWeekEnd),
      type: 'preset'
    },
    'this-month': {
      label: 'This Month',
      start_date: formatLocalDate(new Date(currentYear, currentMonth, 1)),
      end_date: formatLocalDate(new Date(currentYear, currentMonth + 1, 0)),
      type: 'preset'
    },
    'last-month': {
      label: 'Last Month',
      start_date: formatLocalDate(new Date(currentYear, currentMonth - 1, 1)),
      end_date: formatLocalDate(new Date(currentYear, currentMonth, 0)),
      type: 'preset'
    },
    'this-quarter': {
      label: 'This Quarter',
      start_date: formatLocalDate(new Date(currentYear, Math.floor(currentMonth / 3) * 3, 1)),
      end_date: formatLocalDate(new Date(currentYear, Math.floor(currentMonth / 3) * 3 + 3, 0)),
      type: 'preset'
    },
    'ytd': {
      label: 'Year to Date',
      start_date: formatLocalDate(new Date(currentYear, 0, 1)),
      end_date: formatLocalDate(today),
      type: 'preset'
    },
    'last-year': {
      label: 'Last Year',
      start_date: formatLocalDate(new Date(currentYear - 1, 0, 1)),
      end_date: formatLocalDate(new Date(currentYear - 1, 11, 31)),
      type: 'preset'
    },
    // Recent periods
    'last-7-days': {
      label: 'Last 7 Days',
      start_date: formatLocalDate(new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000)),
      end_date: formatLocalDate(today),
      type: 'preset'
    },
    'last-30-days': {
      label: 'Last 30 Days',
      start_date: formatLocalDate(new Date(today.getTime() - 29 * 24 * 60 * 60 * 1000)),
      end_date: formatLocalDate(today),
      type: 'preset'
    },
    'last-90-days': {
      label: 'Last 90 Days',
      start_date: formatLocalDate(new Date(today.getTime() - 89 * 24 * 60 * 60 * 1000)),
      end_date: formatLocalDate(today),
      type: 'preset'
    }
  };
};

const getPresetOptions = (yearsOnly = false, showFutureYears = false) => {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth();

  if (yearsOnly) {
    return generateYearOptions(currentYear, showFutureYears); // ✅ Pass the parameter
  }

  return {
    ...generateStandardPresets(today, currentYear, currentMonth),
    ...generateMonthOptions(currentYear, 'month'),
    ...generateMonthOptions(currentYear - 1, 'prev-month'),
    'custom': {
      label: 'Custom Range',
      type: 'custom'
    }
  };
};

const getGroupedOptions = (yearsOnly = false) => {
  if (yearsOnly) {
    return {
      'Select Year': null // Will be populated with sorted year keys
    };
  }

  return {
    'Current Periods': ['last-week', 'this-month', 'last-month', 'this-quarter', 'ytd', 'last-year'],
    'Recent': ['last-7-days', 'last-30-days', 'last-90-days'],
    'Custom': ['custom']
  };
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const DateRangeSelector = ({ 
  dateRange, 
  onDateRangeChange, 
  className = '', 
  yearsOnly = false,
  showFutureYears = false // ✅ Add this prop
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  const presetOptions = getPresetOptions(yearsOnly, showFutureYears);

  // Find current selection
  const getCurrentSelection = () => {
    for (const [key, option] of Object.entries(presetOptions)) {
      if (option.type !== 'custom' && 
          option.start_date === dateRange.start_date && 
          option.end_date === dateRange.end_date) {
        return key;
      }
    }
    
    if (yearsOnly) {
      const currentYear = new Date().getFullYear();
      return `year-${currentYear}`;
    }
    
    return 'custom';
  };

  const currentSelection = getCurrentSelection();

  useEffect(() => {
    if (currentSelection === 'custom' && !yearsOnly) {
      setCustomStartDate(dateRange.start_date);
      setCustomEndDate(dateRange.end_date);
    }
  }, [dateRange, currentSelection, yearsOnly]);

  const handlePresetSelect = (presetKey) => {
    const preset = presetOptions[presetKey];
    if (preset.type !== 'custom') {
      onDateRangeChange({
        start_date: preset.start_date,
        end_date: preset.end_date,
        preset: presetKey
      });
      setIsOpen(false);
    }
  };

  const handleCustomDateChange = () => {
    if (customStartDate && customEndDate && customStartDate <= customEndDate) {
      onDateRangeChange({
        start_date: customStartDate,
        end_date: customEndDate,
        preset: 'custom'
      });
      setIsOpen(false);
    }
  };

  const formatDisplayDate = () => {
    if (yearsOnly) {
      if (currentSelection && presetOptions[currentSelection]) {
        return presetOptions[currentSelection].label;
      }
      const startYear = new Date(dateRange.start_date).getFullYear();
      return startYear.toString();
    }

    if (currentSelection !== 'custom' && presetOptions[currentSelection]) {
      return presetOptions[currentSelection].label;
    }

    const currentYear = new Date().getFullYear().toString();
    const formatOptions = (dateStr) => ({
      month: 'short',
      day: 'numeric',
      ...(dateStr.split('-')[0] !== currentYear && { year: 'numeric' })
    });

    const start = new Date(dateRange.start_date).toLocaleDateString('en-US', formatOptions(dateRange.start_date));
    const end = new Date(dateRange.end_date).toLocaleDateString('en-US', formatOptions(dateRange.end_date));
    
    return `${start} - ${end}`;
  };

  // Get grouped options with year sorting for years-only mode
  const groupedOptions = (() => {
    const groups = getGroupedOptions(yearsOnly);
    
    if (yearsOnly) {
      const yearKeys = Object.keys(presetOptions).sort((a, b) => {
        const yearA = presetOptions[a].year;
        const yearB = presetOptions[b].year;
        return yearB - yearA; // Sort descending
      });
      return { 'Select Year': yearKeys };
    }
    
    return groups;
  })();

  return (
    <div className={`relative ${className}`}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center justify-start px-3 sm:px-4 py-2 border border-gray-300 rounded-md bg-white text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      >
        <CalendarIcon className="w-4 h-4 mr-2 text-gray-500 flex-shrink-0" />
        <span className="mr-2 whitespace-nowrap">{formatDisplayDate()}</span>
        <ChevronDownIcon className={`w-4 h-4 text-gray-500 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className={`
          absolute 
          left-0 sm:right-0 sm:left-auto
          top-full 
          mt-1 
          w-[calc(100vw-2rem)] sm:w-80 md:${yearsOnly ? 'w-64' : 'w-96'}
          max-w-sm sm:max-w-none
          bg-white 
          border border-gray-200 
          rounded-lg 
          shadow-xl
          z-[100]
        `}>
          <div className="p-3 sm:p-4 max-h-[60vh] sm:max-h-96 overflow-y-auto">
            {Object.entries(groupedOptions).map(([groupName, optionKeys]) => (
              <div key={groupName} className="mb-4 last:mb-0">
                <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2 px-1">
                  {groupName}
                </h4>
                <div className="space-y-1">
                  {optionKeys.map(key => {
                    const option = presetOptions[key];
                    if (!option) return null;
                    
                    if (option.type === 'custom') {
                      return (
                        <div key={key} className="space-y-3 p-3 border rounded-md bg-gray-50">
                          <div className="text-sm font-medium text-gray-700">Custom Date Range</div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Start Date</label>
                              <input
                                type="date"
                                value={customStartDate}
                                onChange={(e) => setCustomStartDate(e.target.value)}
                                className="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">End Date</label>
                              <input
                                type="date"
                                value={customEndDate}
                                onChange={(e) => setCustomEndDate(e.target.value)}
                                className="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                          </div>
                          <button
                            onClick={handleCustomDateChange}
                            disabled={!customStartDate || !customEndDate || customStartDate > customEndDate}
                            className="w-full py-2 px-3 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                          >
                            Apply Custom Range
                          </button>
                        </div>
                      );
                    }

                    return (
                      <button
                        key={key}
                        onClick={() => handlePresetSelect(key)}
                        className={`w-full text-left px-3 py-2 text-sm rounded-md hover:bg-gray-100 transition-colors ${
                          currentSelection === key ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="flex-1">{option.label}</span>
                          {!yearsOnly && option.type !== 'preset' && (
                            <span className="text-xs text-gray-500 flex-shrink-0 hidden sm:inline">
                              {formatDateForDisplay(option.start_date)} - {formatDateForDisplay(option.end_date)}
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Click-away overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-[99]" 
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default DateRangeSelector;